/**
 * useAuth — Zustand store for SSO authentication via app.ars93.com
 *
 * Calls GET /auth/me (proxied to auth-gateway) which reads the httpOnly
 * cookie ars93_access, validates the JWT, and returns the user profile.
 * El access token dura 15 min — un 401 "expired" dispara un intento de
 * refresh (POST /api/auth/refresh, cookie ars93_refresh de 7 días) antes
 * de dar la sesión por perdida.
 */

import { create } from 'zustand';

const PORTAL_LOGIN = 'https://app.ars93.com/login';

export interface AuthUser {
  user_id: string;
  email: string;
  org_id: string;
  org_name: string;
  role: string;
  /** Server-computed convenience: role ∈ {superadmin, org_admin}. */
  is_admin: boolean;
  features: string[];
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'forbidden' | 'error';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;

  fetchMe: () => Promise<void>;
  /** Intenta rotar el access token via el refresh token. true si tuvo éxito. */
  refresh: () => Promise<boolean>;
  logout: () => void;
  hasFeature: (slug: string) => boolean;
  isAdmin: () => boolean;
}

async function fetchMeInternal(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
  afterRefresh: boolean,
): Promise<void> {
  try {
    const res = await fetch('/auth/me', { credentials: 'include' });

    if (res.status === 401) {
      const data = await res.json().catch(() => ({}));
      // Access token vencido (no "no_token") — vale la pena rotar antes
      // de mandar al usuario de vuelta al login del portal.
      if (!afterRefresh && data.error === 'expired') {
        const ok = await get().refresh();
        if (ok) {
          await fetchMeInternal(set, get, true);
          return;
        }
      }
      set({ status: 'unauthenticated', user: null, error: null });
      return;
    }

    if (res.status === 403) {
      const data = await res.json().catch(() => ({}));
      set({ status: 'forbidden', user: null, error: data.error || 'no_app_access' });
      return;
    }

    if (!res.ok) {
      set({ status: 'error', user: null, error: `HTTP ${res.status}` });
      return;
    }

    const user: AuthUser = await res.json();
    set({ status: 'authenticated', user, error: null });
  } catch {
    set({ status: 'error', user: null, error: 'network' });
  }
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  error: null,

  fetchMe: () => fetchMeInternal(set, get, false),

  refresh: async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      return res.ok;
    } catch {
      return false;
    }
  },

  logout: () => {
    // SSO logout: redirect to portal which clears the .ars93.com cookie
    window.location.href = `${PORTAL_LOGIN}?redirect=${encodeURIComponent(window.location.href)}`;
  },

  hasFeature: (slug: string) => {
    const { user } = get();
    return user?.features.includes(slug) ?? false;
  },

  isAdmin: () => {
    const { user } = get();
    return user?.is_admin === true;
  },
}));

/** Redirect to portal login with return URL */
export function redirectToLogin() {
  window.location.href = `${PORTAL_LOGIN}?redirect=${encodeURIComponent(window.location.href)}`;
}
